import { create } from 'zustand';

import { CurrentUser, currentUserApi, PremiumFlag } from '../axios';
// import { SSOApi } from '../axios/sso';
import { LangParam } from '../types';
import { clearAmplitudeIdentity, setAmplitudeIdentity } from '../utils/eventTracker';
import { shutdownIntercom } from '../utils/intercom';
import { useNestedLocationStore } from './nestedLocationStore';

interface UserDataStore {
  permissions: string[];
  featureFlags: string[];
  premiumFlags: Record<string, PremiumFlag>;
  loading: boolean;
  currentUser: CurrentUser | null;
  lang: LangParam;
  fetchedUserData: boolean;
  /**
   * True only when the session lookup concluded there is NO session: a 401 or 403 from
   * `/current_user`. Any other failure - a network drop, a 500, a timeout - leaves it false,
   * because those say nothing about whether the person is logged in.
   *
   * `fetchedUserData` cannot carry that distinction: it is set in the `finally`, so it is true
   * down the rejection path too. Reading "resolved and no user" as "anonymous" therefore treats
   * a failed lookup as proof of anonymity, which is how the share-link bypass stayed open for a
   * logged-in user whose lookup failed.
   */
  userDataUnauthenticated: boolean;
  premiumFeatures: Record<string, string>;

  // Permission checking methods
  hasAdminRoleOnLocations: () => boolean;

  actions: {
    initializeUserData: () => Promise<void>;
    refreshPremiumFlags: () => Promise<void>;
    setLang: (lang: LangParam) => void;
    logoutUser: () => void;
    addFreeFormAnswer: (formAnswerId: number) => void;
    setUiAcknowledgement: (key: string, value: boolean) => void;
    setOpsCenterAccess: (isOpsCenterAdmin: boolean, permissions: string | null) => void;
  };
}

const useUserDataStore = create<UserDataStore>((set, get) => ({
  permissions: [],
  featureFlags: [],
  premiumFlags: {},
  loading: false,
  currentUser: null,
  lang: 'en',
  fetchedUserData: false,
  userDataUnauthenticated: false,
  premiumFeatures: {},
  hasAdminRoleOnLocations: () => {
    const { currentUser, permissions } = get();
    return !!(
      currentUser?.is_admin ||
      currentUser?.is_superadmin ||
      currentUser?.is_support ||
      permissions?.includes('admin_places')
    );
  },

  actions: {
    initializeUserData: async () => {
      if (get().loading) return;
      try {
        set({ loading: true });
        const [permissions, flagsResponse, user] = await Promise.all([
          currentUserApi.getCurrentUserPermissions(),
          currentUserApi.getCurrentUserFlags(),
          currentUserApi.getCurrentUserName(),
        ]);

        set((state) => ({
          permissions,
          featureFlags: flagsResponse.flags,
          premiumFlags: flagsResponse.premium_flags || {},
          premiumFeatures: flagsResponse.premium_features || {},
          currentUser: user,
          loading: false,
          userDataUnauthenticated: false,
          lang: user?.lang || state.lang,
        }));

        // Set Amplitude identity for user and account grouping
        if (user?.id && user?.company_id) {
          setAmplitudeIdentity(user.id, user.company_id);
        }

        if (flagsResponse?.flags?.includes('NestableLocations')) {
          await useNestedLocationStore.getState().actions.loadAllLocations();
        }

        currentUserApi.syncCompanyDataToIntercom();
      } catch (error) {
        console.error('Failed to initialize user data:', error);
        const status = (error as { response?: { status?: number } })?.response?.status;
        set({ loading: false, userDataUnauthenticated: status === 401 || status === 403 });
      } finally {
        set({ fetchedUserData: true });
      }
    },

    refreshPremiumFlags: async () => {
      const flagsResponse = await currentUserApi.getCurrentUserFlags();
      if (Object.keys(flagsResponse.premium_flags).length > 0) {
        set({ premiumFlags: flagsResponse.premium_flags || {} });
      }
    },

    setLang: (lang: LangParam) => set({ lang }),

    logoutUser: () => {
      // try {
      //   // Try to logout from SSO as well
      //   await SSOApi.logoutSSO();
      // } catch (error) {
      //   console.error('SSO logout failed:', error);
      // }

      set({ currentUser: null, permissions: [], featureFlags: [], premiumFlags: {} });
      clearAmplitudeIdentity();
      shutdownIntercom();
    },

    addFreeFormAnswer: (formAnswerId: number) => {
      if (
        get().currentUser?.free_form_answers?.length &&
        get().currentUser?.free_form_answers?.length >= 99
      )
        return;
      set((state) => ({
        currentUser: {
          ...state.currentUser,
          free_form_answers: [...(state.currentUser?.free_form_answers || []), formAnswerId],
        },
      }));
    },

    // Optimistic local mirror of a persisted `acknowledgeUi` call. Without this,
    // `currentUser` only refreshes on a full `initializeUserData()` run (app
    // boot / login), so a preference saved mid-session (e.g. the Plannings view
    // toggle) would revert to its session-start value on remount — navigating
    // away from and back to the module — until the next full reload.
    setUiAcknowledgement: (key: string, value: boolean) => {
      set((state) => ({
        currentUser: state.currentUser
          ? {
              ...state.currentUser,
              ui_acknowledgements: { ...state.currentUser.ui_acknowledgements, [key]: value },
            }
          : state.currentUser,
      }));
    },

    // Same local-mirror reasoning as setUiAcknowledgement, for Ops Center access.
    // The Ops Center re-reads it from the server on entry and on every tab
    // change; mirroring it here keeps the rest of the app (the MainMenu entry)
    // from offering a door the user can no longer open, without waiting for the
    // next full initializeUserData().
    setOpsCenterAccess: (isOpsCenterAdmin: boolean, permissions: string | null) => {
      set((state) => {
        if (!state.currentUser) return state;
        if (
          state.currentUser.isOpsCenterAdmin === isOpsCenterAdmin &&
          state.currentUser.opsCenterPermissions === (permissions ?? undefined)
        ) {
          return state;
        }
        return {
          currentUser: {
            ...state.currentUser,
            isOpsCenterAdmin,
            opsCenterPermissions: permissions ?? undefined,
          },
        };
      });
    },


  },
}));

export { useUserDataStore };
